import type { RecommendedJob } from "@/modules/feedbox/api/types"
import type { NextRequest } from "@/modules/fetch/types"
import type { JobAdDetail } from "@/modules/jobAdDetail/types"

import { validate } from "uuid"

import { getResultFromResponse } from "@/modules/apiRequests/handlers"
import { GET_FEEDBOX_LIST_RECOMMENDATIONS_ENDPOINT } from "@/modules/feedbox/api/constants"
import { handleFetchError } from "@/modules/fetch/errorHandler"
import { fetcher } from "@/modules/fetch/fetcher"
import { logger } from "@/modules/monitoring/logger"

const MAX_SIMILAR_JOB_ADS_DISPLAYED = "4"

function getIdFromSlug(slug: string): string | null {
  const potentialUuid = slug.substring(0, 36)

  if (validate(potentialUuid)) {
    return potentialUuid
  }

  return null
}

type GetSimilarJobAdsParams = {
  jobAdSlug: JobAdDetail["slug"]
  req?: NextRequest
}

export async function getSimilarJobAds({ jobAdSlug, req }: GetSimilarJobAdsParams): Promise<RecommendedJob[]> {
  const searchParams = new URLSearchParams()
  searchParams.set("pageSize", MAX_SIMILAR_JOB_ADS_DISPLAYED)

  const currentJobAdUuid = getIdFromSlug(jobAdSlug)
  if (currentJobAdUuid) {
    searchParams.set("excludeIds", currentJobAdUuid)
  }

  const data = await fetcher(GET_FEEDBOX_LIST_RECOMMENDATIONS_ENDPOINT, {
    nextRequest: req,
    requestInit: {
      headers: {
        "rollout-reco": "true",
        source: "POST_APPLICATION",
      },
    },
    searchParams,
    serviceDomain: "KRAKEND_API",
  })
    .then(getResultFromResponse<{ posts: RecommendedJob[] }>())
    .then(({ posts }) => posts)
    .catch(handleFetchError(GET_FEEDBOX_LIST_RECOMMENDATIONS_ENDPOINT))
    .catch(error => {
      logger.error(`An unhandled error occured while fetching Similar Jobs Recommendations: ${error.message}.`)
      return []
    })

  return data
}
